/**
 * Agent Gateway API Service
 *
 * Communicates with the Agent Gateway for PRS analysis and comparison.
 */

import axios, { AxiosInstance } from 'axios';
import { config } from '../config';
import { oktaAuth } from './oktaAuth';

const GATEWAY_URL = config.gatewayUrl;

// Create axios instance for agent gateway
const createAgentClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: GATEWAY_URL,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    timeout: 300000, // 5 minutes for long-running agent operations
  });

  // Request interceptor to add Authorization header
  client.interceptors.request.use(
    (config) => {
      // Token will be set dynamically per request
      // Check if Authorization header is already set
      if (!config.headers['Authorization'] && agentService.getAccessToken()) {
        config.headers['Authorization'] = `Bearer ${agentService.getAccessToken()}`;
      }

      return config;
    },
    (error) => {
      console.error('[Agent API] Request Error:', error);
      return Promise.reject(error);
    }
  );

  return client;
};

// Agent service class
class AgentService {
  private client: AxiosInstance;
  private accessToken: string | null = null;

  constructor() {
    this.client = createAgentClient();
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const config = error.config;
        if (
          error.response?.status === 401 &&
          config &&
          !(config as Record<string, unknown>).__tokenRefreshRetried
        ) {
          (config as Record<string, unknown>).__tokenRefreshRetried = true;
          try {
            const newToken = await oktaAuth.tokenManager.renew('accessToken') as { accessToken: string } | undefined;
            if (newToken?.accessToken) {
              this.setAccessToken(newToken.accessToken);
              return this.client.request(config);
            }
          } catch {
            // Renew failed; fall through
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Set the access token for API requests
   */
  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  /**
   * Get the current access token
   */
  getAccessToken(): string | null {
    return this.accessToken;
  }

  /**
   * Trigger PRS analysis
   */
  async triggerAnalysis(request: AnalyzeRequest): Promise<JobResponse> {
    const response = await this.client.post(`/api/analyze`, request);
    return response.data;
  }

  /**
   * Trigger PRS comparison
   */
  async triggerComparison(request: CompareRequest): Promise<JobResponse> {
    const response = await this.client.post(`/api/compare`, request);
    return response.data;
  }

  /**
   * Generate requirements from PRS comparison
   */
  async generateRequirements(request: GenerateRequirementsRequest): Promise<JobResponse> {
    const response = await this.client.post(`/api/generate-requirements`, request);
    return response.data;
  }

  /**
   * Generate protocol modification plan (legacy format)
   */
  async generatePlan(request: GeneratePlanRequest): Promise<JobResponse> {
    const response = await this.client.post(`/api/generate-plan`, request);
    return response.data;
  }

  /**
   * Generate protocol modification plan (v2 - session-based)
   */
  async generatePlanV2(request: GeneratePlanV2Request): Promise<JobResponse> {
    const response = await this.client.post(`/api/generate-plan`, request);
    return response.data;
  }

  /**
   * Execute protocol modification plan
   */
  async executeProtocol(request: ExecuteProtocolRequest): Promise<JobResponse> {
    const response = await this.client.post(`/api/execute-plan`, request);
    return response.data;
  }

  /**
   * Get job status and details
   */
  async getJobStatus(jobId: string): Promise<JobDetail> {
    const response = await this.client.get(`/api/jobs/${jobId}`);
    return response.data;
  }

  /**
   * Create WebSocket connection for real-time progress updates (legacy: job-specific)
   */
  createWebSocket(
    jobId: string,
    onMessage: (event: ProgressEvent) => void,
    onError?: (error: Event) => void,
    onClose?: () => void
  ): WebSocket {
    const wsUrl = GATEWAY_URL.replace('http://', 'ws://').replace('https://', 'wss://');
    const ws = new WebSocket(`${wsUrl}/ws/${jobId}`);

    ws.onopen = () => {};

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // Apply status normalization (like Flutter)
        const normalized = normalizeProgressEvent(data);
        onMessage(normalized);
      } catch (error) {
        console.error('[WS] Failed to parse message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('[WS] Error:', error);
      if (onError) onError(error);
    };

    ws.onclose = () => {
      if (onClose) onClose();
    };

    return ws;
  }

  /**
   * Connect to dispatcher WebSocket (no job ID in path).
   *
   * This mirrors Flutter's connectToDispatcher() pattern:
   * 1. Connect FIRST so we don't miss early messages
   * 2. Then send start_job via sendStartJob()
   * 3. Listen for progress events on the same connection
   */
  connectToDispatcher(
    onMessage: (event: ProgressEvent) => void,
    onError?: (error: Event | string) => void,
    onClose?: () => void
  ): WebSocket {
    const wsUrl = GATEWAY_URL.replace('http://', 'ws://').replace('https://', 'wss://');
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {};

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // Log raw WebSocket payload for debugging (status from emit_final_result / emit)
        console.log('[WS Dispatcher] raw message:', {
          keys: Object.keys(data),
          status: data.status,
          payloadStatus: data.payload?.status,
          hasPayload: !!data.payload,
          message: data.message ?? data.payload?.message,
        });
        const normalized = normalizeProgressEvent(data);
        console.log('[WS Dispatcher] normalized event:', { status: normalized.status, message: normalized.message, hasResult: !!normalized.result });
        onMessage(normalized);
      } catch (error) {
        console.error('[WS Dispatcher] Failed to parse message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('[WS Dispatcher] Error:', error);
      if (onError) onError(error);
    };

    ws.onclose = () => {
      if (onClose) onClose();
    };

    return ws;
  }

  /**
   * Send a start_job message over an existing dispatcher connection.
   *
   * Mirrors Flutter's sendStartJob() - waits for WS to be open,
   * then sends the action + payload as a JSON message.
   */
  async sendStartJob(
    ws: WebSocket,
    jobAction: string,
    payload: Record<string, any>
  ): Promise<void> {
    // Wait for WebSocket to be open if not already
    if (ws.readyState === WebSocket.CONNECTING) {
      await new Promise<void>((resolve, reject) => {
        const onOpen = () => {
          ws.removeEventListener('open', onOpen);
          ws.removeEventListener('error', onErr);
          resolve();
        };
        const onErr = () => {
          ws.removeEventListener('open', onOpen);
          ws.removeEventListener('error', onErr);
          reject(new Error('WebSocket failed to connect'));
        };
        ws.addEventListener('open', onOpen);
        ws.addEventListener('error', onErr);
      });
    }

    if (ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not open');
    }

    const message = JSON.stringify({
      action: 'start_job',
      job_action: jobAction,
      ...payload,
    });

    ws.send(message);
  }
}

// ============================================
// STATUS NORMALIZATION (mirrors Flutter)
// ============================================

/**
 * Normalize status strings from the backend to consistent values.
 * Flutter does: 'complete' → 'completed', 'error' → 'failed', 'started' → 'running'
 */
function normalizeStatus(status: string): string {
  switch (status.toLowerCase()) {
    case 'complete':
      return 'completed';
    case 'error':
      return 'failed';
    case 'started':
      return 'running';
    default:
      return status || 'running';
  }
}

/**
 * Normalize a raw WebSocket event into a ProgressEvent with normalized status.
 * If the message is wrapped (e.g. { payload: { status, result } }), use the inner payload.
 */
function normalizeProgressEvent(data: any): ProgressEvent {
  const unwrapped = data?.payload && typeof data.payload === 'object' && (data.payload.status != null || data.payload.result !== undefined)
    ? data.payload
    : data;
  const rawStatus = unwrapped.status ?? data.status ?? '';
  let normalizedStatus = normalizeStatus(rawStatus);
  const message = unwrapped.message ?? unwrapped.error ?? data.message ?? data.error ??
    (normalizedStatus === 'completed' ? 'Job completed'
      : normalizedStatus === 'failed' ? 'Job failed'
        : 'Progress update');
  // Backend sometimes sends status:'running' on error events; detect via message prefix
  if (normalizedStatus === 'running' && message.startsWith('❌')) {
    normalizedStatus = 'failed';
  }

  return {
    job_id: unwrapped.job_id ?? data.job_id ?? data.jobId ?? '',
    status: normalizedStatus,
    message,
    timestamp: unwrapped.timestamp ?? data.timestamp ?? new Date().toISOString(),
    data: unwrapped.data ?? data.data,
    result: unwrapped.result ?? data.result,
  };
}

/**
 * Check if a completed event has a usable result (like Flutter's _hasUsableResult).
 * Returns false for null, empty string, empty objects, empty arrays.
 */
export function hasUsableResult(result: any): boolean {
  if (result == null) return false;
  if (typeof result === 'string') return result.trim().length > 0;
  if (Array.isArray(result)) return result.length > 0;
  if (typeof result === 'object') return Object.keys(result).length > 0;
  return true;
}

// Export singleton instance
const agentService = new AgentService();

export interface AnalyzeRequest {
  trial_id: string;
  version: string;
  platform: 'app' | 'web';
  force?: boolean;
  generate_requirements?: boolean;
}

export interface CompareRequest {
  trial_id: string;
  version1: string;
  version2: string;
  platform: 'app' | 'web';
  force?: boolean;
}

export interface GenerateRequirementsRequest {
  trial_id: string;
  version1: string;
  version2: string;
  platform: 'app' | 'web';
  override?: boolean;
}

export interface GeneratePlanRequest {
  trial_id: string;
  version1: string;
  version2: string;
  platform: 'app' | 'web';
  analysis_context?: string;
  plan_instructions?: string;
}

export interface GeneratePlanV2Request {
  trial_id: string;
  session_id: string;
  platform: 'app' | 'web';
  requirements_url?: string;
  override?: boolean;
  analysis_context?: string | null;
  plan_instructions?: string | null;
}

export interface ExecuteProtocolRequest {
  trial_id: string;
  session_id: string;
  platform: 'app' | 'web';
  plan_url?: string;
}

export interface JobResponse {
  job_id: string;
  status: string;
  created_at: string;
}

export interface JobDetail {
  job_id: string;
  job_type: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  trial_id: string;
  version?: string;
  version1?: string;
  version2?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  result?: any;
  error?: string;
}

export interface ProgressEvent {
  job_id: string;
  status: string;
  message: string;
  timestamp: string;
  data?: any;
  result?: any; // Completion result data (for hasUsableResult validation)
}

// Export singleton instance and legacy function exports for backwards compatibility
export { agentService };

// Legacy exports - delegate to agentService instance
export const triggerAnalysis = (request: AnalyzeRequest) => agentService.triggerAnalysis(request);
export const triggerComparison = (request: CompareRequest) => agentService.triggerComparison(request);
export const generateRequirements = (request: GenerateRequirementsRequest) => agentService.generateRequirements(request);
export const generatePlan = (request: GeneratePlanRequest) => agentService.generatePlan(request);
export const generatePlanV2 = (request: GeneratePlanV2Request) => agentService.generatePlanV2(request);
export const executeProtocol = (request: ExecuteProtocolRequest) => agentService.executeProtocol(request);
export const getJobStatus = (jobId: string) => agentService.getJobStatus(jobId);
export const createWebSocket = (
  jobId: string,
  onMessage: (event: ProgressEvent) => void,
  onError?: (error: Event) => void,
  onClose?: () => void
) => agentService.createWebSocket(jobId, onMessage, onError, onClose);

// New dispatcher-based exports (matching Flutter's pattern)
export const connectToDispatcher = (
  onMessage: (event: ProgressEvent) => void,
  onError?: (error: Event | string) => void,
  onClose?: () => void
) => agentService.connectToDispatcher(onMessage, onError, onClose);
export const sendStartJob = (
  ws: WebSocket,
  jobAction: string,
  payload: Record<string, any>
) => agentService.sendStartJob(ws, jobAction, payload);
